import { PF, calibriWorkbook } from './tagFont';
import { groupPieces } from './tagGroups';
import jsPDF from 'jspdf';
import ExcelJS from 'exceljs';
import { inr } from '@/lib/tagPricing';
import { saleSummary, fineGold } from '@/lib/tagSales';
import { sepCols, sepQty, styleOfLabel, xlStyle, tint } from '@/lib/tagMatHandling';

const SEP = (i) => i.sep;
// Separate weights (e.g. Dak Wt) as extra columns right after the net weight.
const withSep = (cols, rows, at, mk) => { const extra = sepCols(rows, SEP).map(mk); if (!extra.length) return cols; const k = cols.findIndex(at) + 1; return [...cols.slice(0, k), ...extra, ...cols.slice(k)]; };
const colsOf = (lines) => withSep(COLS, lines, (c) => c[0] === 'Net g', (s) => [`${s.label} ${s.unit}`, (i) => sepQty(i, s.label, SEP) || 0, s.unit === 'g' ? '0.000' : '0.00']);

const NAVY = [30, 42, 74]; const GREEN = [15, 107, 79]; const MUTED = [100, 116, 139];
const d8 = (s) => (s ? new Date(s).toLocaleDateString('en-GB') : '');
const rs = (v) => `Rs. ${inr(v)}`;
const rmAmt = (i) => ['dia', 'stone', 'pearl', 'other'].reduce((t, k) => t + (i.mats?.[k]?.amt || 0), 0);
const purTitle = (l) => (!l || l === 'No purity' ? 'No purity' : `${l} Gold`);

const COLS = [
  ['Date', (i) => d8(i.sale.created_at)], ['Customer', (i) => i.sale.customer_name], ['Jewellery no.', (i) => i.jewellery_no],
  ['Type', (i) => i.jewellery_type], ['Purity', (i) => i.purity], ['Gross g', (i) => i.gross, '0.000'], ['Net g', (i) => i.net, '0.000'],
  ['Dia ct', (i) => i.mats?.dia?.ct || 0, '0.00'], ['CS ct', (i) => i.mats?.stone?.ct || 0, '0.00'], ['Pearl ct', (i) => i.mats?.pearl?.ct || 0, '0.00'],
  ['Gold', (i) => i.gold, '#,##0'], ['Raw materials', rmAmt, '#,##0', 1.4], ['Labour', (i) => i.labour, '#,##0'], ['Charges', (i) => i.charges_total, '#,##0'], ['Total', (i) => i.total, '#,##0'],
];
const sumRows = (lines) => { const s = saleSummary(lines); return [...s.groups.map((g) => ({ ...g, title: purTitle(g.label) })), { ...s.grand, title: 'Grand total', grand: true }]; };

function save(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// Customer / sales-person report: every sold tag + purity-wise summary.
const NAVY_X = 'FF1E2A4A'; const GREEN_X = 'FF0F6B4F'; const THIN = { style: 'thin', color: { argb: 'FFD9DEE7' } };

const itemGroups = (lines) => groupPieces(lines, (i) => i.page_no, (i) => i.jewellery_no, (i) => i.jewellery_type);
// Customer -> approval/sale no. groups (Approval Register "all approvals" exports).
const byKey = (ls, key) => { const m = new Map(); ls.forEach((l) => { const k = key(l); if (!m.has(k)) m.set(k, []); m.get(k).push(l); }); return [...m.entries()]; };
const custGroups = (lines) => byKey(lines, (l) => l.sale.customer_name || 'Customer').sort((a, b) => a[0].localeCompare(b[0]))
  .map(([cust, ls]) => [cust, byKey(ls, (l) => l.sale.number).sort((a, b) => String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true }))]);
const custItemGroups = (lines) => custGroups(lines).flatMap(([cust, aps]) => aps.map(([no, ls]) => ({ label: `${cust}  ·  ${d8(ls[0].sale.created_at)}`, items: ls })));
const sumLine = (ls, label) => {
  const S = (f) => ls.reduce((t, i) => t + (Number(f(i)) || 0), 0);
  const mat = (k) => ({ ct: S((i) => i.mats?.[k]?.ct), amt: S((i) => i.mats?.[k]?.amt) });
  return { sub: true, sale: { number: '', created_at: '', customer_name: '' }, jewellery_no: label, jewellery_type: '', purity: '', gross: S((i) => i.gross), net: S((i) => i.net),
    mats: { dia: mat('dia'), stone: mat('stone'), pearl: mat('pearl'), other: mat('other') }, gold: S((i) => i.gold), labour: S((i) => i.labour), charges_total: S((i) => i.charges_total), total: S((i) => i.total), sep: ls.flatMap((i) => i.sep || []) };
};
// Sales-table rows with customer / approval headings and subtotals.
const groupedRows = (lines) => custGroups(lines).flatMap(([cust, aps]) => {
  const all = aps.flatMap(([, ls]) => ls);
  return [{ band: 'cust', text: `${cust}  ·  ${all.length} piece(s)` }, ...aps.flatMap(([no, ls]) => [{ band: "ap", text: `${d8(ls[0].sale.created_at)}  ·  ${ls.length} piece(s)` }, ...ls, { ...sumLine(ls, `Total ${d8(ls[0].sale.created_at)}`), scope: "ap" }]), { ...sumLine(all, `Total ${cust}`), scope: 'cust' }];
});

// One vertical "Raw Material | Weight | Amount in Rs" block per purity (and a grand total block), one under another.
const SUM_ROWS = [['Gross weight (g)', (s) => s.gross, '0.000', () => ''], ['Net weight (g)', (s) => s.net, '0.000', (s) => s.gold],
  ['Diamonds (ct)', (s) => s.dia.ct, '0.00', (s) => s.dia.amt], ['Colour stones (ct)', (s) => s.stone.ct, '0.00', (s) => s.stone.amt],
  ['Pearls (ct)', (s) => s.pearl.ct, '0.00', (s) => s.pearl.amt], ['Other materials (ct)', (s) => s.other.ct, '0.00', (s) => s.other.amt, true],
  ['Labour', () => '', '', (s) => s.labour], ['Other charges', () => '', '', (s) => s.charges], ['Total', () => '', '', (s) => s.total]];
// Item-details kinds each summary row adds up (weight kind, amount kind).
const SUM_KINDS = { 'Gross weight (g)': ['gross'], 'Net weight (g)': ['gold', 'gold'], 'Diamonds (ct)': ['dia', 'dia'], 'Colour stones (ct)': ['stone', 'stone'],
  'Pearls (ct)': ['pearl', 'pearl'], 'Other materials (ct)': ['other', 'other'], Labour: [null, 'labour'], 'Other charges': [null, 'charge'], Total: [null, 'sale'] };
// Summary rows that also carry a piece count (shown when 'with pieces' is ticked).
const PCS_KEY = { 'Diamonds (ct)': 'dia', 'Colour stones (ct)': 'stone', 'Pearls (ct)': 'pearl', 'Other materials (ct)': 'other' };
const fill = (c, argb) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } }; };
const TOP = { style: 'thin', color: { argb: 'FF1E2A4A' } };
const topLine = (c) => { c.border = { ...(c.border || {}), top: TOP }; };
const colL = (n) => { let s = ''; let x = n; while (x > 0) { const m = (x - 1) % 26; s = String.fromCharCode(65 + m) + s; x = Math.floor((x - 1) / 26); } return s; };
const IDS = "'Item details'";
const q = (v) => `"${String(v).replace(/"/g, '""')}"`;
const fx = (formula, result) => ({ formula, result });
const thumbImg = (url) => typeof url === 'string' && url.match(/^data:image\/(png|jpe?g);base64,(.+)$/);

// Item details: each piece line by line (amount = qty x rate, gold and labour = net wt x rate), hidden kind / purity
// columns let the Sales, Summary and Break-up sheets add everything up with formulas.
function addItemDetails(ws, wb, lines, thumbs, showPieces, groupsOf = itemGroups) {
  const head = ['Image', 'Jewellery / Raw material', 'Qty', 'Unit', ...(showPieces ? ['Pcs'] : []), 'Rate in Rs', 'Amount in Rs', 'Group total in Rs', 'Kind', 'Purity', 'Gold 999 g'];
  const RATE = showPieces ? 6 : 5; const AMT = RATE + 1; const GT = RATE + 2; const KIND = GT + 1; const PUR = GT + 2; const FINE = GT + 3;
  const [cR, cA, cK, cP, cF] = [RATE, AMT, KIND, PUR, FINE].map(colL);
  ws.columns = [{ width: 14 }, { width: 34 }, { width: 11 }, { width: 6 }, ...(showPieces ? [{ width: 8 }] : []), { width: 13 }, { width: 15 }, { width: 17 }, { width: 8, hidden: true }, { width: 8, hidden: true }, { width: 10, hidden: true }];
  const h = ws.addRow(head);
  h.eachCell((c) => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY_X } }; });
  ws.views = [{ state: 'frozen', ySplit: 1 }];
  const style = (r, opts = {}) => r.eachCell({ includeEmpty: true }, (c, k) => { if (k > 1 && k <= GT) { c.border = { bottom: THIN }; if (opts.bold) c.font = { bold: true, size: opts.size || 11, color: { argb: opts.color || NAVY_X } }; if (opts.fill) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.fill } }; } });
  // vals: [label, qty, unit, pcs, rate, amount]; kind / purity go to the hidden columns.
  const put = ([label, qty, unit, pcs, rate, amt], opts = {}, kind = '', pur = '') => {
    const r = ws.addRow(['', label, qty, unit, ...(showPieces ? [pcs] : []), rate, amt, null, kind || null, kind ? pur : null]);
    [3, RATE, AMT, GT].forEach((k) => { r.getCell(k).alignment = { horizontal: 'right' }; });
    if (qty !== '') r.getCell(3).numFmt = unit === 'ct' ? '0.00' : '0.000';
    [RATE, AMT, GT].forEach((k) => { r.getCell(k).numFmt = '#,##0'; });
    style(r, opts);
    return r;
  };
  // Amount = ROUND(qty x rate, 0) when there is a rate, else the saved amount.
  // Amount = ROUND(qty x rate, 0) when that gives the saved amount; otherwise the saved amount stays typed in.
  const xr = (v) => Math.round(Number((Number(v) || 0).toPrecision(15)));
  const amtOf = (r, rate, amt, qty) => (rate === '' || rate == null || xr(Number(qty) * Number(rate)) !== Math.round(Number(amt) || 0) ? amt : fx(`ROUND(C${r.number}*${cR}${r.number},0)`, amt));
  const refs = new Map();
  const gtCells = []; let grossAll = 0;
  groupsOf(lines).forEach((g) => {
    const gh = ws.addRow(['', g.label]);
    style(gh, { bold: true, color: 'FFFFFFFF', fill: 'FF475569' });
    const gStart = ws.rowCount + 1; let gTotal = 0; let gGross = 0;
    g.items.forEach((i) => {
      const start = ws.rowCount + 1;
      const pur = i.purity || '';
      put([i.jewellery_no, '', '', '', '', ''], { bold: true, fill: 'FFF3F5F9' });
      const gr = put(['Gross weight', i.gross, 'g', '', '', ''], {}, 'gross', pur);
      const nr = put([`Net weight${i.purity ? ` (${i.purity})` : ''}`, i.net, 'g', '', i.gold_rate, ''], {}, 'gold', pur);
      nr.getCell(AMT).value = amtOf(nr, i.gold_rate, i.gold, i.net);
      nr.getCell(FINE).value = fx(`C${nr.number}*${Number(i.purity_pct) || 0}/100`, (Number(i.net) || 0) * (Number(i.purity_pct) || 0) / 100);
      const ref = { gross: `${IDS}!C${gr.number}`, net: `${IDS}!C${nr.number}`, gold: `${IDS}!${cA}${nr.number}`, lines: [], charges: [] };
      i.lines.forEach((l) => {
        const r = put([l.label, l.ct, l.unit || 'ct', l.pcs || '', l.rate, ''], l.type === 'sep' ? xlStyle(styleOfLabel(l.label)) : {}, l.type === 'sep' ? 'sep' : (l.type || 'other'), pur);
        r.getCell(AMT).value = amtOf(r, l.rate, l.amt, l.ct);
        ref.lines.push({ type: l.type, ct: `${IDS}!C${r.number}`, amt: `${IDS}!${cA}${r.number}` });
      });
      const lr = put(['Labour - per g of net wt', fx(`C${nr.number}`, i.net), 'g', '', i.labour_rate, ''], {}, 'labour', pur);
      lr.getCell(AMT).value = amtOf(lr, i.labour_rate, i.labour, i.net);
      ref.labour = `${IDS}!${cA}${lr.number}`;
      (i.charges || []).forEach((c) => { const r = put([`${c.name} (extra charge)`, '', '', '', '', c.amount], {}, 'charge', pur); ref.charges.push(`${IDS}!${cA}${r.number}`); });
      const parts = (i.gold || 0) + i.lines.reduce((t, l) => t + (Number(l.amt) || 0), 0) + (i.labour || 0) + (i.charges || []).reduce((t, c) => t + (Number(c.amount) || 0), 0);
      const sv = put(['Sale value', '', '', '', '', Math.round(parts) === Math.round(i.total || 0) ? fx(`SUM(${cA}${nr.number}:${cA}${ws.rowCount})`, i.total) : i.total], { bold: true, color: GREEN_X, fill: 'FFE8F3EE' }, 'sale', pur);
      topLine(sv.getCell(AMT));
      ref.total = `${IDS}!${cA}${sv.number}`;
      refs.set(i, ref);
      gTotal += i.total; gGross += i.gross;
      ws.mergeCells(start, 1, ws.rowCount, 1);
      const m = thumbImg(thumbs?.get(i.id));
      if (m) {
        const id = wb.addImage({ base64: m[2], extension: m[1] === 'png' ? 'png' : 'jpeg' });
        ws.addImage(id, { tl: { col: 0.15, row: start - 1 + 0.2 }, ext: { width: 80, height: 80 } });
        for (let r = start; r <= ws.rowCount; r += 1) if ((ws.rowCount - start + 1) * 15 < 90) ws.getRow(r).height = 90 / (ws.rowCount - start + 1);
      }
    });
    const gEnd = ws.rowCount;
    const rng = (c) => `${c}${gStart}:${c}${gEnd}`;
    const gt = put([`Group total (${g.items.length} piece${g.items.length === 1 ? '' : 's'}) · gross weight`, fx(`SUMIFS(${rng('C')},${rng(cK)},"gross")`, gGross), 'g', '', '', '']);
    gt.getCell(GT).value = fx(`SUMIFS(${rng(cA)},${rng(cK)},"sale")`, gTotal);
    style(gt, { bold: true, fill: 'FFFEF3C7' });
    topLine(gt.getCell(GT));
    gtCells.push(`${colL(GT)}${gt.number}`); grossAll += gGross;
    ws.addRow([]);
  });
  // Bounded range (not C:C) so the grand-total cell is not inside its own range (Excel circular reference).
  const lastRow = ws.rowCount;
  const g = put([`Grand total (${lines.length} piece${lines.length === 1 ? '' : 's'}) · gross weight`, fx(`SUMIFS(C2:C${lastRow},${cK}2:${cK}${lastRow},"gross")`, grossAll), 'g', '', '', '']);
  g.getCell(GT).value = fx(gtCells.length ? `SUM(${gtCells.join(',')})` : '0', lines.reduce((t, i) => t + (i.total || 0), 0));
  style(g, { bold: true, size: 12, color: 'FFFFFFFF', fill: GREEN_X });
  topLine(g.getCell(GT));
  // Whole-column ranges on this sheet for SUMIFS elsewhere.
  const col = (c) => `${IDS}!$${c}:$${c}`;
  return { refs, qty: col('C'), amt: col(cA), kind: col(cK), pur: col(cP), fine: col(cF), pcs: showPieces ? col('E') : null };
}

// Sales sheet cells pointing at Item details (sep columns stay values).
const SALE_REF = {
  'Gross g': (r) => r.gross, 'Net g': (r) => r.net, Gold: (r) => r.gold, Labour: (r) => r.labour, Total: (r) => r.total,
  'Dia ct': (r) => r.lines.filter((l) => l.type === 'dia').map((l) => l.ct).join('+') || '0',
  'CS ct': (r) => r.lines.filter((l) => l.type === 'stone').map((l) => l.ct).join('+') || '0',
  'Pearl ct': (r) => r.lines.filter((l) => l.type === 'pearl').map((l) => l.ct).join('+') || '0',
  'Raw materials': (r) => r.lines.filter((l) => l.type !== 'sep').map((l) => l.amt).join('+') || '0',
  Charges: (r) => r.charges.join('+') || '0',
};

function addSummary(ws, lines, X) {
  ws.addRow(['Summary']).font = { bold: true, size: 13, color: { argb: NAVY_X } };
  const rows = sumRows(lines);
  const sumif = (rangeAll, kind, s) => `SUMIFS(${rangeAll},${X.kind},"${kind}"${s.grand ? '' : `,${X.pur},${q(s.label)}`})`;
  (rows.length === 2 ? rows.slice(0, 1).map((r) => ({ ...r, title: `${r.title} · total` })) : rows).forEach((s) => {
    const t = ws.addRow([`${s.title} · ${s.count} piece${s.count === 1 ? '' : 's'}`]);
    t.getCell(1).font = { bold: true, color: { argb: s.grand ? NAVY_X : GREEN_X } };
    const NC = X.pcs ? 4 : 3; const CI = Array.from({ length: NC }, (_, k) => k + 1);
    const h = ws.addRow(['Raw Material', 'Weight', 'Amount in Rs', ...(X.pcs ? ['Pcs'] : [])]);
    CI.forEach((k) => { const c = h.getCell(k); c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; fill(c, s.grand ? NAVY_X : GREEN_X); if (k > 1) c.alignment = { horizontal: 'right' }; });
    SUM_ROWS.filter(([, w, , amt, optional]) => !optional || w(s) || amt(s)).forEach(([label, w, fmt, amt]) => {
      const [wk, ak] = SUM_KINDS[label];
      const pk = X.pcs && PCS_KEY[label];
      const r = ws.addRow([label, wk ? fx(sumif(X.qty, wk, s), w(s)) : '', ak ? fx(sumif(X.amt, ak, s), amt(s)) : '', ...(pk ? [fx(sumif(X.pcs, wk, s), s[pk].pcs)] : [])]);
      if (fmt) r.getCell(2).numFmt = fmt;
      r.getCell(3).numFmt = '#,##0';
      const last = label === 'Total';
      CI.forEach((ci) => { const c = r.getCell(ci); c.border = { bottom: THIN }; if (last) { c.font = { bold: true, color: { argb: NAVY_X } }; fill(c, 'FFE8F3EE'); } });
    });
    // Thick dark outline around each purity block.
    const THICK = { style: 'medium', color: { argb: NAVY_X } };
    for (let rn = h.number; rn <= ws.rowCount; rn += 1) {
      CI.forEach((ci) => {
        const c = ws.getRow(rn).getCell(ci);
        c.border = { ...c.border, ...(rn === h.number ? { top: THICK } : {}), ...(rn === ws.rowCount ? { bottom: THICK } : {}), ...(ci === 1 ? { left: THICK } : {}), ...(ci === NC ? { right: THICK } : {}) };
      });
    }
    ws.addRow([]);
  });
}

// Break-up: Gold 999 (weight × rate), other materials, labour and misc (other charges), each in its own row.
function addBreakup(ws, lines, X) {
  const f = fineGold(lines);
  const sum = (fn) => lines.reduce((t, i) => t + (fn(i) || 0), 0);
  const S = (kinds) => kinds.map((k) => `SUMIFS(${X.amt},${X.kind},"${k}")`).join('+');
  const h = ws.addRow(['Break-up', 'Gold 999 (g)', 'Rate in Rs / g', 'Amount in Rs']);
  [1, 2, 3, 4].forEach((k) => { const c = h.getCell(k); c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; fill(c, NAVY_X); if (k > 1) c.alignment = { horizontal: 'right' }; });
  const first = ws.rowCount + 1;
  [[`Gold 999${f.mixed ? ' (average rate)' : ''}`, fx(`ROUND(SUM(${X.fine}),3)`, f.fg), f.rate, fx(S(['gold']), f.gold), 'FFFEF3C7'],
    ['Other materials (diamonds, stones, pearls)', '', '', fx(S(['dia', 'stone', 'pearl', 'other']), sum(rmAmt)), 'FFE0F2FE'],
    ['Labour', '', '', fx(S(['labour']), sum((i) => i.labour)), 'FFEDE9FE'], ['Misc items (other charges)', '', '', fx(S(['charge']), sum((i) => i.charges_total)), 'FFFCE7F3'],
    ['Grand total', '', '', fx(S(['sale']), f.total), 'FFD1FAE5']].forEach(([l, g, rate, amt, argb], k) => {
    const r = ws.addRow([l, g, rate, amt]);
    r.getCell(2).numFmt = '0.000'; r.getCell(3).numFmt = '#,##0'; r.getCell(4).numFmt = '#,##0';
    [1, 2, 3, 4].forEach((ci) => { const c = r.getCell(ci); fill(c, argb); c.font = { bold: k === 4, size: k === 4 ? 12 : 11, color: { argb: NAVY_X } }; c.border = { bottom: THIN }; });
    r.height = 22;
  });
  return first;
}

export async function exportSalesExcel(title, subtitle, lines, thumbs, { showPieces = false, byCustomer = false } = {}) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Sales');
  const X = addItemDetails(wb.addWorksheet('Item details'), wb, lines, thumbs, showPieces, byCustomer ? custItemGroups : itemGroups);
  ws.addRow([title]).font = { bold: true, size: 14, color: { argb: 'FF1E2A4A' } };
  ws.addRow([subtitle]).font = { color: { argb: 'FF64748B' } };
  ws.addRow([]);
  addSummary(ws, lines, X);
  addBreakup(ws, lines, X);
  ws.addRow([]);
  ws.addRow([]);
  // Details table: image in column A, data from column B.
  const cols = colsOf(lines); const NC = cols.length + 1;
  const h = ws.addRow(['Image', ...cols.map((c) => c[0])]);
  h.eachCell((c) => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E2A4A' } }; });
  const numeric = cols.map((c) => !!c[2]);
  let apRows = []; let custSubs = [];
  (byCustomer ? groupedRows(lines) : lines).forEach((i) => {
    if (i.band) {
      if (i.band === 'cust') custSubs = []; else apRows = [];
      const r = ws.addRow([i.text]); r.font = { bold: true, color: { argb: i.band === 'cust' ? 'FFFFFFFF' : 'FF1E2A4A' } }; for (let k = 1; k <= NC; k += 1) fill(r.getCell(k), i.band === 'cust' ? 'FF475569' : 'FFE2E8F0'); return;
    }
    const r = ws.addRow(['', ...cols.map((c) => c[1](i))]); cols.forEach((c, k) => { if (c[2]) r.getCell(k + 2).numFmt = c[2]; });
    const ref = !i.sub && X.refs.get(i);
    if (ref) cols.forEach((c, k) => { if (SALE_REF[c[0]]) r.getCell(k + 2).value = fx(SALE_REF[c[0]](ref), c[1](i)); });
    if (i.sub) {
      const over = i.scope === 'cust' ? custSubs : apRows;
      cols.forEach((c, k) => { if (!numeric[k] || !over.length) return; const L = colL(k + 2); const cell = r.getCell(k + 2); cell.value = fx(`SUM(${over.map((n) => `${L}${n}`).join(',')})`, c[1](i)); });
      if (i.scope === 'ap') custSubs.push(r.number);
      r.font = { bold: true }; for (let k = 1; k <= NC; k += 1) fill(r.getCell(k), 'FFFEF3C7');
      return;
    }
    apRows.push(r.number);
    const m = thumbImg(thumbs?.get(i.id));
    if (m) {
      r.height = 48;
      ws.addImage(wb.addImage({ base64: m[2], extension: m[1] === 'png' ? 'png' : 'jpeg' }), { tl: { col: 0.2, row: r.number - 1 + 0.1 }, ext: { width: 56, height: 56 }, editAs: 'oneCell' });
    }
    for (let k = 1; k <= NC; k += 1) { const c = r.getCell(k); c.alignment = { ...(c.alignment || {}), vertical: 'middle' }; }
  });
  ws.columns.forEach((c, k) => { c.width = k === 0 ? 30 : k < 7 ? 16 : 14; });
  save(new Blob([await calibriWorkbook(wb).xlsx.writeBuffer()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${title}.xlsx`);
}


function table(pdf, y, cols0, rows, { W, H, MX, head, fill, thumbs }) {
  const bw = W - 2 * MX; const RH = 6.5; const IRH = 13;
  // With thumbs: an Image column first and taller item rows (text vertically centred).
  const cols = thumbs ? [['Image', () => '', '', 0.9], ...cols0] : cols0;
  const wt = cols.map((c) => c[3] || (/Jewellery|Customer/.test(c[0]) ? 1.9 : c[0] === '#' ? 0.4 : /ct$/.test(c[0]) ? 0.75 : 1));
  const unit = bw / wt.reduce((a, b) => a + b, 0);
  const xs = wt.reduce((a, w, k) => [...a, a[k] + w * unit], [MX]);
  const isText = (c) => !c[2];
  const tx = (c, k) => (isText(c) ? xs[k] + 1.5 : xs[k + 1] - 1.5);
  const header = () => {
    pdf.setFillColor(...(fill || NAVY)); pdf.rect(MX, y, bw, RH + 1, 'F');
    pdf.setFont(PF(), 'bold'); pdf.setFontSize(6.8); pdf.setTextColor(255, 255, 255);
    cols.forEach((c, k) => pdf.text(c[0], tx(c, k), y + 4.7, { align: isText(c) ? 'left' : 'right' }));
    y += RH + 1;
  };
  header();
  rows.forEach((r, ri) => {
    const im = thumbs && !r.band && !r.sub && !r.grand && thumbs.get(r.id); const m = thumbImg(im);
    const rh = m ? IRH : RH;
    if (y + rh > H - 12) { pdf.addPage(); y = head(); header(); }
    const by = y + rh / 2 + 1.15;
    if (r.band) {
      pdf.setFillColor(...(r.band === 'cust' ? [71, 85, 105] : [226, 232, 240])); pdf.rect(MX, y, bw, RH, 'F');
      pdf.setFont(PF(), 'bold'); pdf.setFontSize(7.5); pdf.setTextColor(...(r.band === 'cust' ? [255, 255, 255] : NAVY)); pdf.text(r.text, MX + 1.5, y + 4.4);
      y += RH; return;
    }
    if (r.sub) { pdf.setFillColor(254, 243, 199); pdf.rect(MX, y, bw, rh, 'F'); pdf.setFont(PF(), 'bold'); pdf.setFontSize(7); pdf.setTextColor(...NAVY); } else
    if (r.grand) { pdf.setFillColor(232, 243, 238); pdf.rect(MX, y, bw, rh, 'F'); } else if (ri % 2) { pdf.setFillColor(248, 250, 252); pdf.rect(MX, y, bw, rh, 'F'); }
    if (m) { try { pdf.addImage(im, m[1] === 'png' ? 'PNG' : 'JPEG', MX + 1, y + 1, IRH - 2, IRH - 2); } catch { /* skip a bad image */ } }
    pdf.setFont(PF(), r.grand || r.sub ? 'bold' : 'normal'); pdf.setFontSize(7); pdf.setTextColor(...(r.grand ? GREEN : NAVY));
    cols.forEach((c, k) => {
      const v = c[1](r); const t = typeof v === 'number' ? (c[2] === '0.000' ? v.toFixed(3) : c[2] === '0.00' ? v.toFixed(2) : inr(v)) : String(v ?? '');
      pdf.text(pdf.splitTextToSize(t, (xs[k + 1] - xs[k]) - 2)[0] || '', tx(c, k), by, { align: isText(c) ? 'left' : 'right' });
    });
    y += rh;
  });
  return y;
}

// Summary blocks (Raw Material | Weight | Amount in Rs), up to 3 side by side per row.
function pdfSummary(pdf, y, lines, { W, H, MX, head, pcs }) {
  const rows = sumRows(lines);
  const blocks = rows.length === 2 ? [{ ...rows[0], title: `${rows[0].title} · total` }] : rows;
  const GAP = 6; const bw = (W - 2 * MX - 2 * GAP) / 3; const RH = 6;
  if (y > H - 40) { pdf.addPage(); y = head(); }
  pdf.setFont(PF(), 'bold'); pdf.setFontSize(12); pdf.setTextColor(...NAVY); pdf.text('Summary', MX, y); y += 3;
  for (let k = 0; k < blocks.length; k += 3) {
    const chunk = blocks.slice(k, k + 3).map((s) => ({ s, items: SUM_ROWS.filter(([, w, , amt, optional]) => !optional || w(s) || amt(s)) }));
    const bh = 6 + RH + 1 + Math.max(...chunk.map((c) => c.items.length)) * RH;
    if (y + bh > H - 12) {
      pdf.addPage(); y = head();
      pdf.setFont(PF(), 'bold'); pdf.setFontSize(12); pdf.setTextColor(...NAVY); pdf.text('Summary (contd.)', MX, y); y += 3;
    }
    chunk.forEach(({ s, items }, n) => {
      const x = MX + n * (bw + GAP); const c2 = x + bw * 0.75; let yy = y;
      const color = s.grand ? NAVY : GREEN;
      pdf.setFont(PF(), 'bold'); pdf.setFontSize(8.5); pdf.setTextColor(...color);
      pdf.text(`${s.title} · ${s.count} piece${s.count === 1 ? '' : 's'}`, x, yy + 4); yy += 6;
      pdf.setFillColor(...color); pdf.rect(x, yy, bw, RH + 1, 'F');
      pdf.setFontSize(7); pdf.setTextColor(255, 255, 255);
      pdf.text('Raw Material', x + 1.5, yy + 4.6); pdf.text('Weight', c2 - 1.5, yy + 4.6, { align: 'right' }); pdf.text('Amount in Rs', x + bw - 1.5, yy + 4.6, { align: 'right' });
      yy += RH + 1;
      items.forEach(([label, w, fmt, amt], ri) => {
        const total = label === 'Total';
        if (total) { pdf.setFillColor(232, 243, 238); pdf.rect(x, yy, bw, RH, 'F'); } else if (ri % 2) { pdf.setFillColor(248, 250, 252); pdf.rect(x, yy, bw, RH, 'F'); }
        const wv = w(s); const av = amt(s);
        pdf.setFont(PF(), total ? 'bold' : 'normal'); pdf.setFontSize(7); pdf.setTextColor(...(total ? GREEN : NAVY));
        pdf.text(label, x + 1.5, yy + 4.2);
        const pc = pcs && PCS_KEY[label] ? s[PCS_KEY[label]].pcs : 0;
        if (wv !== '') pdf.text(`${fmt === '0.000' ? Number(wv).toFixed(3) : Number(wv).toFixed(2)}${pc ? ` ct · ${pc} pcs` : ''}`, c2 - 1.5, yy + 4.2, { align: 'right' });
        if (av !== '') pdf.text(inr(av), x + bw - 1.5, yy + 4.2, { align: 'right' });
        yy += RH;
      });
      pdf.setDrawColor(...NAVY); pdf.setLineWidth(0.6); pdf.rect(x, y + 6, bw, yy - y - 6); pdf.setLineWidth(0.2);
    });
    y += bh + 6;
  }
  return y;
}

// PDF Item details: grouped like the Excel sheet; an item never splits across pages; column headers on every page.
function pdfItemDetails(pdf, lines, thumbs, showPieces, { W, H, MX, head }, groupsOf = itemGroups) {
  const RH = 5.6; const IMG = 26; const bw = W - 2 * MX;
  const fixed = [['Image', 30], ['Qty', 22, 'r'], ['Unit', 12], ...(showPieces ? [['Pcs', 14, 'r']] : []), ['Rate in Rs', 30, 'r'], ['Amount in Rs', 34, 'r'], ['Group total in Rs', 36, 'r']];
  const cols = [fixed[0], ['Jewellery / Raw material', bw - fixed.reduce((t, x) => t + x[1], 0)], ...fixed.slice(1)];
  const xs = cols.reduce((a, col, k) => [...a, a[k] + col[1]], [MX]);
  const at = (name) => cols.findIndex((col) => col[0] === name);
  const txt = (k, v, y, bold) => {
    if (k < 0 || v === '' || v === undefined || v === null) return;
    pdf.setFont(PF(), bold ? 'bold' : 'normal');
    const right = cols[k][2] === 'r';
    pdf.text(pdf.splitTextToSize(String(v), cols[k][1] - 2)[0] || '', right ? xs[k + 1] - 1.5 : xs[k] + 1.5, y + 3.9, { align: right ? 'right' : 'left' });
  };
  const header = (y) => {
    pdf.setFillColor(...NAVY); pdf.rect(MX, y, bw, RH + 1, 'F');
    pdf.setFontSize(7); pdf.setTextColor(255, 255, 255);
    cols.forEach((col, k) => txt(k, col[0], y + 0.4, true));
    return y + RH + 1;
  };
  const newPage = () => { pdf.addPage(); const y = head(); pdf.setFont(PF(), 'bold'); pdf.setFontSize(11); pdf.setTextColor(...NAVY); pdf.text('Item details', MX, y + 2); return header(y + 4); };
  const band = (y, fill) => { pdf.setFillColor(...fill); pdf.rect(MX + cols[0][1], y, bw - cols[0][1], RH, 'F'); };
  const rowsOf = (i) => [
    { label: i.jewellery_no, head: true },
    { label: 'Gross weight', qty: i.gross.toFixed(3), unit: 'g' },
    { label: `Net weight${i.purity ? ` (${i.purity})` : ''}`, qty: i.net.toFixed(3), unit: 'g', rate: inr(i.gold_rate), amt: inr(i.gold) },
    ...i.lines.map((l) => ({ label: l.label, qty: Number(l.ct).toFixed(l.unit === 'g' ? 3 : 2), unit: l.unit || 'ct', pcs: l.pcs || '', rate: inr(l.rate), amt: inr(l.amt), st: l.type === 'sep' ? styleOfLabel(l.label) : null })),
    { label: 'Labour - per g of net wt', qty: i.net.toFixed(3), unit: 'g', rate: inr(i.labour_rate), amt: inr(i.labour) },
    ...(i.charges || []).map((ch) => ({ label: `${ch.name} (extra charge)`, amt: inr(ch.amount) })),
    { label: 'Sale value', amt: inr(i.total), sale: true },
  ];
  const drawRow = (y, r) => {
    if (r.head) band(y, [243, 245, 249]); else if (r.sale) band(y, [232, 243, 238]); else if (r.st?.highlight) band(y, tint(r.st.hex));
    pdf.setFontSize(7); pdf.setTextColor(...(r.sale ? GREEN : r.st?.color ? tint(r.st.hex, 0) : NAVY));
    const b = r.head || r.sale || r.st?.bold;
    txt(1, r.label, y, b); txt(at('Qty'), r.qty, y); txt(at('Unit'), r.unit, y); txt(at('Pcs'), r.pcs, y); txt(at('Rate in Rs'), r.rate, y); txt(at('Amount in Rs'), r.amt, y, r.sale);
    pdf.setDrawColor(217, 222, 231); pdf.setLineWidth(0.15); pdf.line(MX + cols[0][1], y + RH, MX + bw, y + RH);
    return y + RH;
  };
  const totalRow = (y, label, gross, amt, fill, color) => {
    pdf.setFillColor(...fill); pdf.rect(MX + cols[0][1], y, bw - cols[0][1], RH + 0.6, 'F');
    pdf.setFontSize(7.5); pdf.setTextColor(...color);
    txt(1, label, y + 0.3, true); txt(at('Qty'), gross.toFixed(3), y + 0.3, true); txt(at('Unit'), 'g', y + 0.3, true); txt(at('Group total in Rs'), inr(amt), y + 0.3, true);
    return y + RH + 0.6;
  };
  let y = newPage();
  let grand = 0; let grandGross = 0;
  groupsOf(lines).forEach((g) => {
    let gTotal = 0; let gGross = 0;
    g.items.forEach((i, n) => {
      const rows = rowsOf(i);
      const h = Math.max(rows.length * RH, IMG + 2) + (n === 0 ? RH : 0);
      const contd = y + h > H - 12 && n > 0;
      if (y + h + (contd ? RH : 0) > H - 12) y = newPage();
      if (n === 0 || contd) {
        pdf.setFillColor(71, 85, 105); pdf.rect(MX, y, bw, RH, 'F'); pdf.setFontSize(7.5); pdf.setTextColor(255, 255, 255);
        txt(1, `${g.label}${n ? ' (contd.)' : ''}`, y, true); y += RH;
      }
      const top = y;
      const url = thumbs?.get(i.id);
      const m = typeof url === 'string' && url.match(/^data:image\/(png|jpe?g);base64,/);
      if (m) { try { pdf.addImage(url, m[1] === 'png' ? 'PNG' : 'JPEG', MX + 2, top + 1, IMG, IMG); } catch { /* skip a bad image */ } }
      rows.forEach((r) => { y = drawRow(y, r); });
      y = Math.max(y, top + IMG + 2);
      gTotal += i.total; gGross += i.gross;
    });
    if (y + RH + 3 > H - 12) y = newPage();
    y = totalRow(y, `Group total (${g.items.length} piece${g.items.length === 1 ? '' : 's'}) · gross weight`, gGross, gTotal, [254, 243, 199], NAVY) + 3;
    grand += gTotal; grandGross += gGross;
  });
  if (y + RH + 1 > H - 12) y = newPage();
  totalRow(y, `Grand total (${lines.length} piece${lines.length === 1 ? '' : 's'}) · gross weight`, grandGross, grand, GREEN, [255, 255, 255]);
}

function pageFooters(pdf, W, H) {
  const n = pdf.getNumberOfPages();
  for (let p = 1; p <= n; p += 1) {
    pdf.setPage(p);
    pdf.setFont(PF(), 'normal'); pdf.setFontSize(8); pdf.setTextColor(...MUTED);
    pdf.text(`Page ${p} of ${n}`, W / 2, H - 5, { align: 'center' });
  }
}

export function exportSalesPdf(title, subtitle, lines, thumbs, { showPieces = false, byCustomer = false } = {}) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' }); pdf.setFont(PF());
  const W = 297; const H = 210; const MX = 10;
  const head = () => {
    pdf.setFont(PF(), 'bold'); pdf.setFontSize(14); pdf.setTextColor(...NAVY); pdf.text(title, MX, 13);
    pdf.setFont(PF(), 'normal'); pdf.setFontSize(8.5); pdf.setTextColor(...MUTED); pdf.text(subtitle, MX, 18.5);
    return 23;
  };
  const opts = { W, H, MX, head };
  let y = pdfSummary(pdf, head() + 4, lines, { ...opts, pcs: showPieces });
  const f = fineGold(lines);
  const sum = (fn) => lines.reduce((t, i) => t + (fn(i) || 0), 0);
  const brk = [{ l: `Gold 999${f.mixed ? ' (average rate)' : ''}`, g: f.fg, rate: f.rate, amt: f.gold }, { l: 'Other materials (diamonds, stones, pearls)', g: '', rate: '', amt: sum(rmAmt) },
    { l: 'Labour', g: '', rate: '', amt: sum((i) => i.labour) }, { l: 'Misc items (other charges)', g: '', rate: '', amt: sum((i) => i.charges_total) }, { l: 'Grand total', g: '', rate: '', amt: f.total, grand: true }];
  if (y > H - 50) { pdf.addPage(); y = head(); }
  pdf.setFont(PF(), 'bold'); pdf.setFontSize(12); pdf.setTextColor(...NAVY); pdf.text('Break-up', MX, y); y += 3;
  y = table(pdf, y, [['Break-up', (r) => r.l, '', 3], ['Gold 999 (g)', (r) => r.g, '0.000'], ['Rate in Rs / g', (r) => r.rate, '#'], ['Amount in Rs', (r) => r.amt, '#']], brk, { ...opts, W: 2 * MX + 170 }) + 10;
  if (y > H - 40) { pdf.addPage(); y = head(); }
  table(pdf, y, colsOf(lines), byCustomer ? groupedRows(lines) : lines, { ...opts, thumbs: thumbs || new Map() });
  pdfItemDetails(pdf, lines, thumbs, showPieces, opts, byCustomer ? custItemGroups : itemGroups);
  pageFooters(pdf, W, H);
  pdf.save(`${title}.pdf`);
}

// Invoice item columns: [header lines, value(i), kind] — kind: t = text, w3 / w2 = weights, m = money.
const INV_COLS = (withOther) => [[['Sr.', 'No.'], (i) => i.n, 't'], [['Jewellery No.'], (i) => i.jewellery_no, 't'], [['Gold', 'Purity'], (i) => i.purity, 't'],
  [['Gross', 'Wt (g)'], (i) => i.gross, 'w3'], [['Net', 'Wt (g)'], (i) => i.net, 'w3'], [['Gold', 'Value'], (i) => i.gold, 'm'],
  [['Diamond', 'Wt (ct)'], (i) => i.mats?.dia?.ct || 0, 'w2'], [['Diamond', 'Value'], (i) => i.mats?.dia?.amt || 0, 'm'],
  [['Col. Stone', 'Wt (ct)'], (i) => i.mats?.stone?.ct || 0, 'w2'], [['Col. Stone', 'Value'], (i) => i.mats?.stone?.amt || 0, 'm'],
  [['Pearls', 'Wt (ct)'], (i) => i.mats?.pearl?.ct || 0, 'w2'], [['Pearls', 'Value'], (i) => i.mats?.pearl?.amt || 0, 'm'],
  ...(withOther ? [[['Other', 'Value'], (i) => i.mats?.other?.amt || 0, 'm']] : []),
  [['Labour', 'Amount'], (i) => i.labour, 'm'], [['Charges', 'Amount'], (i) => i.charges_total, 'm'], [['Total', 'Amount'], (i) => i.total, 'm']];
const invFmt = (v, kind) => (kind === 't' ? String(v ?? '') : kind === 'w3' ? Number(v || 0).toFixed(3) : kind === 'w2' ? Number(v || 0).toFixed(2) : inr(v || 0));

// Column widths that fit the text at font size fs (mm).
function invWidths(pdf, cols, rows, fs) {
  pdf.setFontSize(fs);
  return cols.map(([h, get, kind]) => {
    pdf.setFont(PF(), 'bold'); const hw = Math.max(...h.map((t) => pdf.getTextWidth(t)));
    pdf.setFont(PF(), 'normal'); const vw = Math.max(0, ...rows.map((r) => pdf.getTextWidth(invFmt(get(r), kind))));
    return Math.max(hw, vw) + 2.4;
  });
}

// A4 invoice of one sold list (returned pieces left out); portrait when the item table fits, else landscape. Optional GST %.
export function invoicePdf(sale, company, gstPct = 0, thumbs) {
  const items = sale.items.filter((i) => !i.returned);
  const sumOf = (fn) => items.reduce((t, i) => t + (Number(fn(i)) || 0), 0);
  const cols = withSep(INV_COLS(items.some((i) => i.mats?.other?.amt)), items, (c) => c[0][0] === 'Net', (s) => [[s.label, `Wt (${s.unit})`], (i) => (i.grand ? sepQty({ sep: items.flatMap(SEP) }, s.label, SEP) : sepQty(i, s.label, SEP)), s.unit === 'g' ? 'w3' : 'w2']);
  const rows = items.map((i, n) => ({ ...i, n: String(n + 1) }));
  const grand = { n: '', jewellery_no: 'Grand total', purity: '', grand: true, gross: sumOf((i) => i.gross), net: sumOf((i) => i.net), gold: sumOf((i) => i.gold), labour: sumOf((i) => i.labour), charges_total: sumOf((i) => i.charges_total), total: sumOf((i) => i.total),
    mats: Object.fromEntries(['dia', 'stone', 'pearl', 'other'].map((k) => [k, { ct: sumOf((i) => i.mats?.[k]?.ct), amt: sumOf((i) => i.mats?.[k]?.amt) }])) };
  const FS = 6.6;
  const probe = new jsPDF({ unit: 'mm', format: 'a4' }); probe.setFont(PF());
  const need = invWidths(probe, cols, [...rows, grand], FS);
  const portrait = need.reduce((a, b) => a + b, 0) <= 210 - 20;
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: portrait ? 'portrait' : 'landscape' }); pdf.setFont(PF());
  const W = portrait ? 210 : 297; const H = portrait ? 297 : 210; const MX = 10; const bw = W - 2 * MX;
  const scale = bw / need.reduce((a, b) => a + b, 0);
  const ws = need.map((w) => w * Math.max(1, scale));
  const xs = ws.reduce((acc, w, k) => [...acc, acc[k] + w], [MX]);
  pdf.setFillColor(...NAVY); pdf.rect(0, 0, W, 30, 'F');
  pdf.setFont(PF(), 'bold'); pdf.setFontSize(17); pdf.setTextColor(255, 255, 255); pdf.text(company || 'Sales', MX, 14);
  pdf.setFont(PF(), 'normal'); pdf.setFontSize(9); pdf.text('INVOICE', MX, 21);
  pdf.setFont(PF(), 'bold'); pdf.setFontSize(11); pdf.text(sale.bill_no ? `Invoice No. ${sale.bill_no}` : "", W - MX, 14, { align: 'right' });
  pdf.setFont(PF(), 'normal'); pdf.setFontSize(9); pdf.text(`Date ${d8(sale.created_at)}`, W - MX, 21, { align: 'right' });
  let y = 40;
  pdf.setTextColor(...MUTED); pdf.setFontSize(8); pdf.text('BILL TO', MX, y); pdf.text('SALES PERSON', W / 2 + 10, y);
  pdf.setTextColor(...NAVY); pdf.setFont(PF(), 'bold'); pdf.setFontSize(11.5); pdf.text(sale.customer_name, MX, y + 6);
  pdf.setFont(PF(), 'normal'); pdf.setFontSize(10); pdf.text((sale.sales_names || []).join(', ') || '-', W / 2 + 10, y + 6);
  pdf.setFontSize(8); pdf.setTextColor(...MUTED);
  pdf.text(`${sale.gold_rate_100 ? `Gold rate (100%): Rs. ${inr(sale.gold_rate_100)}/g${sale.rate_card_name ? ` · Rate card: ${sale.rate_card_name}` : ''} · ` : ''}Amounts in Rs`, MX, y + 12);
  y += 18;
  const HH = 9; const RH = 6;
  const header = () => {
    pdf.setFillColor(...NAVY); pdf.rect(MX, y, bw, HH, 'F');
    pdf.setFont(PF(), 'bold'); pdf.setFontSize(FS); pdf.setTextColor(255, 255, 255);
    cols.forEach(([h, , kind], k) => h.forEach((t, li) => pdf.text(t, kind === 't' ? xs[k] + 1.2 : xs[k + 1] - 1.2, y + 3.6 + li * 2.9, { align: kind === 't' ? 'left' : 'right' })));
    y += HH;
  };
  header();
  [...rows, grand].forEach((r, ri) => {
    if (y + RH > H - 14) { pdf.addPage(); y = 14; header(); }
    if (r.grand) { pdf.setFillColor(232, 243, 238); pdf.rect(MX, y, bw, RH + 0.6, 'F'); } else if (ri % 2) { pdf.setFillColor(248, 250, 252); pdf.rect(MX, y, bw, RH, 'F'); }
    pdf.setFont(PF(), r.grand ? 'bold' : 'normal'); pdf.setFontSize(FS); pdf.setTextColor(...(r.grand ? GREEN : NAVY));
    cols.forEach(([, get, kind], k) => pdf.text(invFmt(get(r), kind), kind === 't' ? xs[k] + 1.2 : xs[k + 1] - 1.2, y + 4.1, { align: kind === 't' ? 'left' : 'right' }));
    y += r.grand ? RH + 0.6 : RH;
  });
  const sub = grand.total;
  const tax = Math.round((sub * (Number(gstPct) || 0)) / 100);
  const tot = [['Sub total', sub], ...(tax ? [[`GST ${gstPct}%`, tax]] : []), ['Total', sub + tax]];
  if (y + tot.length * 8 + 20 > H - 12) { pdf.addPage(); y = 20; }
  y += 8;
  tot.forEach(([l, v], k) => {
    const last = k === tot.length - 1;
    if (last) { pdf.setFillColor(...GREEN); pdf.rect(W - MX - 80, y - 5, 80, 9, 'F'); pdf.setTextColor(255, 255, 255); } else pdf.setTextColor(...NAVY);
    pdf.setFont(PF(), last ? 'bold' : 'normal'); pdf.setFontSize(last ? 11 : 9.5);
    pdf.text(l, W - MX - 76, y + 0.8); pdf.text(rs(v), W - MX - 3, y + 0.8, { align: 'right' });
    y += last ? 11 : 7;
  });
  // Small photos of the pieces with their jewellery numbers.
  const pics = items.map((i) => ({ no: i.jewellery_no, url: thumbs?.get(i.id) })).filter((x) => typeof x.url === 'string' && /^data:image\/(png|jpe?g);base64,/.test(x.url));
  if (pics.length) {
    const T = 22; const GAP = 4; const per = Math.floor((bw + GAP) / (T + GAP)); const CH = T + 9;
    if (y + 8 + CH > H - 16) { pdf.addPage(); y = 14; }
    pdf.setFont(PF(), 'bold'); pdf.setFontSize(9); pdf.setTextColor(...NAVY); pdf.text('Pieces', MX, y + 4); y += 7;
    pics.forEach((x, k) => {
      if (k && k % per === 0) { y += CH; if (y + CH > H - 16) { pdf.addPage(); y = 14; } }
      const px = MX + (k % per) * (T + GAP);
      try { pdf.addImage(x.url, /^data:image\/png/.test(x.url) ? 'PNG' : 'JPEG', px, y, T, T); } catch { /* skip a bad image */ }
      pdf.setDrawColor(217, 222, 231); pdf.setLineWidth(0.2); pdf.rect(px, y, T, T);
      pdf.setFont(PF(), 'normal'); pdf.setTextColor(...NAVY);
      let fs = 6; pdf.setFontSize(fs);
      while (fs > 4 && pdf.getTextWidth(x.no) > T) { fs -= 0.25; pdf.setFontSize(fs); }
      pdf.text(pdf.splitTextToSize(x.no, T).slice(0, 2), px + T / 2, y + T + 3, { align: 'center' });
    });
  }
  pdf.setPage(pdf.getNumberOfPages());
  pdf.setFont(PF(), 'italic'); pdf.setFontSize(8); pdf.setTextColor(...MUTED);
  pdf.text(`${items.length} piece${items.length === 1 ? '' : 's'} · Thank you for your business.`, MX, H - 10);
  pageFooters(pdf, W, H);
  pdf.save(`Invoice - ${sale.customer_name}.pdf`.replace(/[\\/:*?"<>|]/g, "-"));
}
